import React, { useState, useCallback } from 'react';
import { Field, reduxForm, untouch, touch } from 'redux-form';
import { useDispatch } from 'react-redux';
import { i18n } from '@international/mastodon-i18n';
import { trackAnalyticsEvent } from '../../../actions';
import { ANALYTICS_EVENTS } from '../../../lib/analytics';
import { MultilineText } from '../../fields';
import * as V from '../../../lib/validations';
import SimpleDocumentList from '../../fields/SimpleDocumentList';
import UploadButton from '../../fields/UploadButton';
import {
  ModalFooter,
  ModalHeader,
  DocumentSectionTitle,
  generateDateComponentsToString,
} from './DocumentVerificationComponents';
import { CandidateExplanationModalProps } from './utils/interfaces';

import './DocumentInfoModal.scss';

const MAX_CANDIDATE_EXPLANATION_LENGTH = 250;
const CANDIDATE_EXPLANATION = 'candidate_explanation';

const CandidateExplanationModal: React.FC<CandidateExplanationModalProps> = ({
  model,
  documentsRequired,
  policy,
  candidateExplanation,
  setCandidateExplanation,
  setIsModalOpen,
  handleSubmitExplanation,
  endDate,
  additionalDocuments,
  onRemoveByUploadId,
  handleModalUploadSuccess,
}) => {
  const dispatch = useDispatch();
  const [isReviewSection, setisReviewSection] = useState(false);
  const [remainingCharacterCount, setRemainingCharacterCount] = useState(
    MAX_CANDIDATE_EXPLANATION_LENGTH - candidateExplanation.length,
  );
  const handleCandidateExplanationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCandidateExplanation(e.target.value);
      setRemainingCharacterCount(
        MAX_CANDIDATE_EXPLANATION_LENGTH - e.target.value.length,
      );
    },
    [setCandidateExplanation],
  );

  const onContinue = () => {
    setisReviewSection(true);
  };

  const onSubmit = async () => {
    if (remainingCharacterCount < MAX_CANDIDATE_EXPLANATION_LENGTH) {
      setIsModalOpen(false);
      await handleSubmitExplanation();
      const submitButton = document.querySelector(
        '[data-testid="submitButton"]',
      ) as HTMLButtonElement;
      submitButton?.click();
    } else {
      dispatch(touch('candidateExplanationForm', CANDIDATE_EXPLANATION));
    }
  };

  const onCancel = () => {
    setCandidateExplanation('');
    dispatch(untouch('candidateExplanationForm', CANDIDATE_EXPLANATION));
    dispatch(
      trackAnalyticsEvent(ANALYTICS_EVENTS.MULTIDOC_EXPLANATION_CLOSED, {
        documentsRequired: documentsRequired.length,
        policy,
      }),
    );
    setIsModalOpen(false);
  };

  const reviewContent = generateDateComponentsToString(
    documentsRequired,
    policy,
    endDate,
  );

  return (
    <div className='modal-explanation'>
      <div className='modal-content'>
        <ModalHeader
          onCancel={onCancel}
          textContent={i18n.getStr(
            'components.employment.missingRequiredDocuments',
          )}
        />
        <div className='modal-body'>
          <DocumentSectionTitle
            titleKey='components.Verification.EmploymentMultidocPanel.candidateExplanation'
            reviewKey={
              isReviewSection
                ? 'components.Verification.EmploymentMultidocPanel.candidateExplanationReview'
                : 'components.Verification.EmploymentMultidocPanel.uploadOtherDocuments'
            }
            reviewContent={reviewContent}
          />
          {isReviewSection ? (
            <Field
              name={CANDIDATE_EXPLANATION}
              component={MultilineText}
              maxLength={MAX_CANDIDATE_EXPLANATION_LENGTH}
              onChange={handleCandidateExplanationChange}
              validate={[V.required]}
              remainingCharacterCount={remainingCharacterCount}
            />
          ) : (
            <UploadButton
              name={model}
              kind='secondary'
              hideIcon
              buttonText='buttons.uploadDocument'
              buttonClass=''
              context='multi-documents'
              fileOptions={{ maxFiles: documentsRequired.length + 10 }}
              handleUploadSuccess={handleModalUploadSuccess}
            />
          )}
          {additionalDocuments.length > 0 && (
            <>
              <DocumentSectionTitle
                titleKey='components.employment.otherDocuments'
                headingLevel='h3'
              />
              <SimpleDocumentList
                onRemoveByUploadId={onRemoveByUploadId}
                documents={additionalDocuments}
                useTagStyle
              />
            </>
          )}
        </div>
        <ModalFooter
          onCancel={onCancel}
          handleSubmit={isReviewSection ? onSubmit : onContinue}
          button={isReviewSection ? 'buttons.confirm' : 'buttons.continue'}
        />
      </div>
    </div>
  );
};

export default reduxForm<{}, CandidateExplanationModalProps>({
  form: 'candidateExplanationForm',
})(CandidateExplanationModal);
